import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Alert, IconButton, Tooltip } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { OpsJobsQueue } from "../../components/OpsJobsQueue";
import { MyJobsQueue } from "../../components/MyJobsQueue";
import { AllJobsView } from "../../components/AllJobsView";
import { JobDetailsDrawer } from "../../components/jobs/JobDetailsDrawer";
import { useAuth } from "../../contexts/AuthContext";

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reviewer-workspace-tabpanel-${index}`}
      aria-labelledby={`reviewer-workspace-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `reviewer-workspace-tab-${index}`,
    "aria-controls": `reviewer-workspace-tabpanel-${index}`,
  };
}

export const OpsJobsPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Tab state management with URL synchronization
  const [tabValue, setTabValue] = useState(0);

  // Job details panel state
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedFirmId, setSelectedFirmId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Check if user has access
  const hasAccess = user?.role === "admin" || user?.role === "reviewer";

  // URL hash to tab mapping
  const hashToTab: Record<string, number> = {
    "#unassigned": 0,
    "#my-worklist": 1,
    "#active-jobs": 2,
    "#completed-jobs": 3,
  };

  const tabToHash: Record<number, string> = {
    0: "#unassigned",
    1: "#my-worklist",
    2: "#active-jobs",
    3: "#completed-jobs",
  };

  // Initialize tab from URL hash
  useEffect(() => {
    const hash = location.hash;
    if (hash && hashToTab[hash] !== undefined) {
      setTabValue(hashToTab[hash]);
    }
  }, [location.hash]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setDetailsOpen(false);
    navigate({ hash: tabToHash[newValue] }, { replace: true });
  };

  const handleRefresh = () => {
    // Refresh logic will be implemented when we add the data fetching hooks
    window.location.reload();
  };

  // Job selection handlers
  const handleJobSelected = (jobId: string, firmId: string) => {
    setSelectedJobId(jobId);
    setSelectedFirmId(firmId);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedJobId(null);
    setSelectedFirmId(null);
  };

  // Show unauthorized message if user doesn't have access
  if (!isAuthenticated || !hasAccess) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Unauthorized - You need REVIEWER or ADMIN access to view this page
        </Alert>
      </Box>
    );
  }

  // Use 'temporary' for small screens (e.g., mobile/tablet)
  // Use 'panel' (or 'permanent') for large screens
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const drawerVariant = isSmallScreen ? "temporary" : "panel";

  const jobDetailsDrawer = (
    <JobDetailsDrawer
      open={detailsOpen}
      onClose={handleDetailsClose}
      jobId={selectedJobId}
      firmId={selectedFirmId}
      variant={drawerVariant}
      context="ops"
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        flexDirection: "column",
        background: "#FFF",
      }}
    >
      {/* Tabs Header */}
      <Box px={3} pt={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", flex: 1 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="reviewer workspace tabs"
            >
              <Tab label="Unassigned" {...a11yProps(0)} />
              <Tab label="My Worklist" {...a11yProps(1)} />
              <Tab label="Active Jobs" {...a11yProps(2)} />
              <Tab label="Completed Jobs" {...a11yProps(3)} />
            </Tabs>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} sx={{ ml: 2 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main content area with flexible layout */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left Panel - Job Lists */}
        <Box
          sx={{
            flex: detailsOpen ? "0 1 60%" : "1 1 100%",
            transition: "flex 0.3s ease-in-out",
            display: "flex",
            flexDirection: "column",
            pr: detailsOpen ? 1 : 0, // Add padding-right when details panel is open
            height: "100%",
            overflow: "auto",
          }}
        >
          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            <OpsJobsQueue
              onJobSelected={handleJobSelected}
              selectedJobId={selectedJobId}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <MyJobsQueue
              onJobSelected={handleJobSelected}
              selectedJobId={selectedJobId}
              context="ops"
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <AllJobsView
              onJobSelected={handleJobSelected}
              selectedJobId={selectedJobId}
              context="ops"
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <AllJobsView
              onJobSelected={handleJobSelected}
              selectedJobId={selectedJobId}
              showOnlyInactive={true}
              context="ops"
            />
          </TabPanel>
        </Box>

        {/* Right Panel - Job Details */}
        {detailsOpen && selectedJobId && selectedFirmId && (
          <>
            {drawerVariant === "temporary" ? (
              jobDetailsDrawer
            ) : (
              <Box
                sx={{
                  flex: "0 0 40%",
                  borderLeft: 1,
                  borderColor: "divider",
                  height: "100%",
                  overflow: "auto",
                }}
              >
                {jobDetailsDrawer}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
